// src/providers/ErrorDialogProvider.js
import React, { useEffect } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom, Provider as JotaiProvider } from 'jotai';
import { atomWithReset, RESET } from 'jotai/utils';
import ErrorDialog from '../components/dialogs/ErrorDialog';

/**
 * エラーダイアログの設定
 */
const ERROR_DIALOG_EVENT = 'show-error-dialog';

/**
 * Jotai Atoms - 状態管理
 */
// ダイアログの開閉状態
const dialogOpenAtom = atom(false);

// エラーデータ
const errorDataAtom = atomWithReset(null);

// ダイアログ設定
const dialogConfigAtom = atomWithReset({});

// デフォルト設定
const defaultConfigAtom = atom({});

// 組み合わせた状態（読み取り専用）
const dialogStateAtom = atom((get) => ({
  isOpen: get(dialogOpenAtom),
  errorData: get(errorDataAtom),
  config: { ...get(defaultConfigAtom), ...get(dialogConfigAtom) }
}));

// アクション用のatom（書き込み専用）
const showErrorActionAtom = atom(
  null,
  (get, set, errorData) => {
    const errorType = determineErrorType(errorData.error, errorData.statusCode);
    const suggestion = generateSuggestion(errorType, errorData.statusCode);
    
    set(errorDataAtom, {
      ...errorData,
      type: errorType,
      suggestion: errorData.suggestion || suggestion
    });
    set(dialogConfigAtom, errorData.customConfig || {});
    set(dialogOpenAtom, true);
  }
);

const hideErrorActionAtom = atom(
  null,
  (get, set) => {
    set(dialogOpenAtom, false);
    // 少し遅延してからデータをクリア（アニメーション用）
    setTimeout(() => {
      set(errorDataAtom, RESET);
      set(dialogConfigAtom, RESET);
    }, 300);
  }
);

/**
 * エラータイプの判定ユーティリティ
 */
const determineErrorType = (error, statusCode) => {
  // セッション期限切れ
  if (statusCode === 401) {
    return 'SESSION_EXPIRED';
  }
  
  // サーバーエラー (5xx)
  if (statusCode >= 500) {
    return 'SERVER_ERROR';
  }
  
  // バリデーションエラー (422)
  if (statusCode === 422) {
    return 'VALIDATION_ERROR';
  }
  
  // その他のクライアントエラー (4xx)
  if (statusCode >= 400) {
    return 'CLIENT_ERROR';
  }
  
  // タイムアウトエラー
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'TIMEOUT_ERROR';
  }
  
  // ネットワークエラー
  if (error?.code === 'NETWORK_ERROR' || !statusCode) {
    return 'NETWORK_ERROR';
  }
  
  return 'DEFAULT';
};

/**
 * エラーメッセージの提案を生成
 */
const generateSuggestion = (errorType, statusCode) => {
  switch (errorType) {
    case 'SERVER_ERROR':
      return 'サーバーに一時的な問題が発生している可能性があります。しばらく時間をおいて再度お試しください。';
    
    case 'CLIENT_ERROR':
      if (statusCode === 403) {
        return 'この操作を実行する権限がありません。管理者に問い合わせてください。';
      } else if (statusCode === 404) {
        return '要求されたリソースが見つかりません。URLやIDが正しいか確認してください。';
      } else if (statusCode === 409) {
        return 'データの競合が発生しました。ページを更新して最新の状態で再度お試しください。';
      }
      return '入力内容を確認して再度お試しください。';
    
    case 'VALIDATION_ERROR':
      return '入力されたデータに問題があります。各項目の入力要件を確認してください。';
    
    case 'NETWORK_ERROR':
      return 'ネットワーク接続を確認し、インターネットに正しく接続されているか確認してください。';
    
    case 'TIMEOUT_ERROR':
      return 'リクエストの処理に時間がかかっています。ネットワーク環境を確認するか、しばらく時間をおいて再度お試しください。';
    
    case 'SESSION_EXPIRED':
      return 'セキュリティのため、一定時間操作がないとセッションが期限切れになります。再度ログインしてください。';
    
    default:
      return '問題が続く場合は、サポートまでお問い合わせください。';
  }
};

/**
 * グローバルイベントハンドラーコンポーネント
 */
const GlobalEventHandler = () => {
  const showError = useSetAtom(showErrorActionAtom);

  useEffect(() => {
    const handleShowErrorDialog = (event) => {
      showError(event.detail);
    };

    window.addEventListener(ERROR_DIALOG_EVENT, handleShowErrorDialog);
    
    return () => {
      window.removeEventListener(ERROR_DIALOG_EVENT, handleShowErrorDialog);
    };
  }, [showError]);

  return null; // レンダリングしない
};

/**
 * エラーダイアログコンポーネント（Jotai統合版）
 */
const ErrorDialogWithAtoms = () => {
  const dialogState = useAtomValue(dialogStateAtom);
  const hideError = useSetAtom(hideErrorActionAtom);

  return (
    <ErrorDialog
      isOpen={dialogState.isOpen}
      onClose={hideError}
      errorData={dialogState.errorData}
      customConfig={dialogState.config}
    />
  );
};

/**
 * エラーダイアログプロバイダーコンポーネント
 */
const ErrorDialogProvider = ({ children, defaultConfig = {} }) => {
  const setDefaultConfig = useSetAtom(defaultConfigAtom);

  // デフォルト設定を初期化
  useEffect(() => {
    setDefaultConfig(defaultConfig);
  }, [defaultConfig, setDefaultConfig]);

  return (
    <JotaiProvider>
      {children}
      <GlobalEventHandler />
      <ErrorDialogWithAtoms />
    </JotaiProvider>
  );
};

/**
 * エラーダイアログを使用するためのフック
 */
export const useErrorDialog = () => {
  const isOpen = useAtomValue(dialogOpenAtom);
  const showError = useSetAtom(showErrorActionAtom);
  const hideError = useSetAtom(hideErrorActionAtom);

  return {
    showError,
    hideError,
    isOpen
  };
};

/**
 * 個別のatom hook（詳細制御用）
 */
export const useErrorDialogState = () => {
  return useAtomValue(dialogStateAtom);
};

export const useErrorDialogActions = () => {
  const showError = useSetAtom(showErrorActionAtom);
  const hideError = useSetAtom(hideErrorActionAtom);
  
  return { showError, hideError };
};

// 読み取り専用フック
export const useIsErrorDialogOpen = () => {
  return useAtomValue(dialogOpenAtom);
};

// エラーデータのみのフック
export const useErrorData = () => {
  return useAtomValue(errorDataAtom);
};

/**
 * グローバルにエラーダイアログを表示するヘルパー関数
 * （BaseServiceなどのコンポーネント外からも使用可能）
 */
export const showGlobalErrorDialog = (errorData) => {
  const event = new CustomEvent(ERROR_DIALOG_EVENT, {
    detail: errorData
  });
  window.dispatchEvent(event);
};

/**
 * エラーオブジェクトからダイアログ用のデータを構築するヘルパー
 */
export const buildErrorDialogData = (error, context = {}) => {
  const { response, config, code, message } = error;
  
  // エラーメッセージの抽出
  let errorMessage = 'エラーが発生しました。';
  
  if (response?.data) {
    const data = response.data;
    
    if (data.message) {
      errorMessage = data.message;
    } else if (data.error) {
      errorMessage = data.error;
    } else if (data.detail) {
      if (typeof data.detail === 'string') {
        errorMessage = data.detail;
      } else if (Array.isArray(data.detail)) {
        errorMessage = data.detail.map(err => {
          if (typeof err === 'string') return err;
          if (err.msg) return `${err.loc ? err.loc.join('.') + ': ' : ''}${err.msg}`;
          return JSON.stringify(err);
        }).join('\n');
      }
    } else if (typeof data === 'string') {
      errorMessage = data;
    }
  } else if (message) {
    errorMessage = message;
  }

  return {
    message: errorMessage,
    error: error,
    errorId: context.errorId || error.errorId,
    statusCode: response?.status,
    endpoint: config?.url || context.endpoint,
    duration: context.duration,
    method: config?.method || context.method,
    customConfig: context.customConfig,
    suggestion: context.suggestion,
    onRetry: context.onRetry
  };
};

/**
 * 成功メッセージ用のダイアログ表示
 */
export const showSuccessDialog = (message, options = {}) => {
  const dialogData = {
    message,
    type: 'SUCCESS',
    customConfig: {
      title: '成功',
      className: 'error-dialog--success',
      bgColor: '#f0fdf4',
      borderColor: '#22c55e',
      ...options.customConfig
    },
    ...options
  };
  
  showGlobalErrorDialog(dialogData);
};

/**
 * 確認ダイアログの表示
 */
export const showConfirmDialog = (message, onConfirm, options = {}) => {
  const dialogData = {
    message,
    type: 'CONFIRMATION',
    customConfig: {
      title: '確認',
      className: 'error-dialog--confirm',
      bgColor: '#fefce8',
      borderColor: '#eab308',
      ...options.customConfig
    },
    onRetry: onConfirm,
    ...options
  };
  
  showGlobalErrorDialog(dialogData);
};

/**
 * 警告ダイアログの表示
 */
export const showWarningDialog = (message, options = {}) => {
  const dialogData = {
    message,
    type: 'WARNING',
    customConfig: {
      title: '警告',
      className: 'error-dialog--warning',
      bgColor: '#fffbeb',
      borderColor: '#f59e0b',
      ...options.customConfig
    },
    ...options
  };
  
  showGlobalErrorDialog(dialogData);
};

/**
 * 情報ダイアログの表示
 */
export const showInfoDialog = (message, options = {}) => {
  const dialogData = {
    message,
    type: 'INFO',
    customConfig: {
      title: '情報',
      className: 'error-dialog--info',
      bgColor: '#f0f9ff',
      borderColor: '#3b82f6',
      ...options.customConfig
    },
    ...options
  };
  
  showGlobalErrorDialog(dialogData);
};

/**
 * カスタムAPIエラー表示
 */
export const showCustomApiError = (error, customOptions = {}) => {
  const dialogData = buildErrorDialogData(error, {
    ...customOptions,
    customConfig: {
      ...customOptions.customConfig
    }
  });
  
  showGlobalErrorDialog(dialogData);
};

/**
 * 状態をリセットするヘルパー（テスト用など）
 */
export const resetErrorDialog = () => {
  const event = new CustomEvent('reset-error-dialog');
  window.dispatchEvent(event);
};

// デバッグ用：状態を取得（開発環境でのみ使用）
export const getErrorDialogDebugInfo = () => {
  if (process.env.NODE_ENV === 'development') {
    return {
      atoms: {
        dialogOpenAtom,
        errorDataAtom,
        dialogConfigAtom,
        defaultConfigAtom
      }
    };
  }
  return null;
};

export default ErrorDialogProvider;